/**
 * Leg normalization — ported verbatim from `@dotpe/rista-pos-common`'s
 * `paymentHelpers.getNewPaymentInfo` / `getUpdatedPaymentAmount`, which the v1
 * flow uses. Ported rather than imported for two reasons: that package's ESM
 * build only resolves inside a bundler (extensionless specifiers), so importing
 * it would make this module unusable — and untestable — in node/kiosk/offline
 * contexts; and per THE RULE payment logic belongs in packages/core anyway.
 *
 * Behaviour must stay identical to the v1 copies until v1 is deleted. The
 * self-check pins the rules that matter.
 */
import type { PaymentLeg } from "./types";

/**
 * ⚠️ **ARITHMETIC — never display-config-driven.** This decides what is CHARGED and SENT, so it
 * stays on two decimals whatever a merchant's DISPLAY precision is set to. What a screen shows
 * goes through `@rista/core/utils/currency`'s `amount()` / `money()` instead; wire that in here
 * and the day a merchant is configured for three, the amount charged stops matching the quote.
 */
export function round2(value: number): number {
  return parseFloat((Math.round((Number(value) || 0) * 100) / 100).toFixed(2));
}

/**
 * Cash keeps whatever was tendered (that overpayment becomes change); every other
 * mode is clamped to the outstanding balance.
 *
 * The two v1 copies of this function had FORKED: apps/web's
 * (`paymenthelper.ts:38`) reads `selectedLeg?.originalAmount || selectedLeg.amount`
 * while rista-pos-common's reads `selectedLeg.amount`. `originalAmount` is the
 * pre-rounding value TouchInput stashes (`TouchInput:171`) and exists only on
 * web — apps/mobile never writes it — so the web form below collapses to the
 * library form on mobile and is behaviour-preserving for both.
 *
 * `||` not `??` on purpose: an `originalAmount` of 0 must fall through to
 * `amount`, as it does in v1.
 */
export function getUpdatedPaymentAmount(
  isCash: boolean | undefined,
  selectedLeg: Partial<PaymentLeg> | undefined,
  balanceAmount: number
): number {
  if (!selectedLeg || Object.keys(selectedLeg).length === 0) return balanceAmount;
  const previousAmount = selectedLeg.originalAmount || selectedLeg.amount || 0;
  if (isCash || previousAmount <= balanceAmount) return previousAmount;
  return balanceAmount;
}

export interface NormalizedLegs {
  /** what goes on the wire */
  paymentsArr: PaymentLeg[];
  /** collected — excludes unposted External legs */
  amount: number;
  /** collected + still in flight */
  collectedAndRequestedAmount: number;
}

/**
 * Drops legs with no amount, zeroes legs that settle asynchronously and have not
 * posted yet (money is not ours until the provider says so) and rounds to 2dp.
 *
 * `settlesAsync` defaults to v1's test — `type === 'External'`, verbatim from
 * `getNewPaymentInfo` — so this file stays the port the equivalence harness checks.
 * Core passes a wider one; see `normalizeLegs` in ./calculate for why.
 */
export function normalizePayments(
  legs: PaymentLeg[],
  settlesAsync: (leg: PaymentLeg) => boolean = (leg) => leg.type === "External"
): NormalizedLegs {
  const paymentsArr: PaymentLeg[] = [];
  let amount = 0;
  let requestedAmount = 0;

  legs.forEach((leg) => {
    if (!leg.amount && !leg.requestAmount) return;
    const data: PaymentLeg = { ...leg };
    if (settlesAsync(data)) {
      data.requestAmount = round2(leg.requestAmount ?? 0);
      if (!data.posted) {
        data.amount = 0;
        if (data.paymentStatus === "Requested" || data.paymentStatus === "Pending") {
          requestedAmount += data.requestAmount;
        }
      }
    } else {
      data.amount = round2(leg.amount);
    }
    amount += data.amount;
    paymentsArr.push(data);
  });

  return {
    paymentsArr,
    amount: round2(amount),
    collectedAndRequestedAmount: round2(requestedAmount + amount),
  };
}
